import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { value: 'before_the_sale', label: 'Before the Sale', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'during_the_sale', label: 'During the Sale', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'after_the_sale', label: 'After the Sale', color: 'bg-blue-100 text-blue-800 border-blue-300' },
];

const STATUS_OPTIONS = [
  { value: 'on_track', label: 'On Track', emoji: '🟢' },
  { value: 'off_track', label: 'Off Track', emoji: '🟡' },
  { value: 'stalled', label: 'Stalled', emoji: '🔴' },
];

function getCategoryInfo(cat) {
  return CATEGORIES.find(c => c.value === cat) || { label: cat, color: 'bg-gray-100 text-gray-800 border-gray-300' };
}

function getStatusInfo(status) {
  return STATUS_OPTIONS.find(s => s.value === status) || { label: status, emoji: '⚪' };
}

function getDefaultDueDate() {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  const endMonth = quarter * 3;
  const endDate = new Date(now.getFullYear(), endMonth, 0);
  return endDate.toISOString().split('T')[0];
}

function getCurrentQuarterLabel() {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${quarter}`;
}

// Create Rock Modal
function CreateRockModal({ onClose, onCreated, existingCategories, token }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [dueDate, setDueDate] = useState(getDefaultDueDate());
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const availableCategories = CATEGORIES.filter(c => !existingCategories.includes(c.value));

  useEffect(() => {
    if (availableCategories.length > 0 && !category) {
      setCategory(availableCategories[0].value);
    }
  }, [availableCategories, category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter a name'); return; }
    if (!category) { setError('Please select a category'); return; }
    if (!dueDate) { setError('Please set a due date'); return; }

    setSubmitting(true);
    try {
      const res = await fetch('/api/rocks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), category, due_date: dueDate }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create rock');
      onCreated(data.rock);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Create Quarterly Rock</h2>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            {availableCategories.length === 0 ? (
              <p className="text-sm text-gray-500">All categories have rocks this quarter.</p>
            ) : (
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="rock-category-select"
              >
                {availableCategories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rock Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Implement referral tracking system"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="rock-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="rock-due-date-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || availableCategories.length === 0}
              className="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              data-testid="rock-submit-btn"
            >
              {submitting ? 'Creating...' : 'Create Rock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Check-in Modal
function CheckinModal({ rock, onClose, onCheckinCreated, token }) {
  const [status, setStatus] = useState('on_track');
  const [actionTaken, setActionTaken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/rocks/${rock.id}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, action_taken: actionTaken.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create check-in');
      onCheckinCreated(rock.id, data.checkin);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-1">Weekly Check-in</h2>
        <p className="text-sm text-gray-500 mb-4">{rock.name}</p>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                    status === opt.value
                      ? 'border-primary bg-primary bg-opacity-10 text-primary ring-2 ring-primary ring-opacity-30'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`checkin-status-${opt.value}`}
                >
                  {opt.emoji} {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Move-forward action taken this week
            </label>
            <textarea
              value={actionTaken}
              onChange={e => setActionTaken(e.target.value)}
              placeholder="What progress did you make on this rock?"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="checkin-action-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              data-testid="checkin-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Check-in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Rock Card Component
function RockCard({ rock, checkins, onCheckin, onComplete, token, isCurrentQuarter }) {
  const catInfo = getCategoryInfo(rock.category);
  const isCompleted = rock.is_completed === 1;

  return (
    <div className={`bg-white rounded-xl shadow-sm border p-5 ${isCompleted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`} data-testid={`rock-card-${rock.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${catInfo.color} mb-2`}>
            {catInfo.label}
          </span>
          <h3 className="text-lg font-semibold text-gray-800">{rock.name}</h3>
        </div>
        {isCompleted && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
            ✅ Completed {rock.completed_on_time ? '(On Time!)' : '(Late)'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
        <span>📅 Due: {rock.due_date}</span>
        <span>⏳ {rock.weeks_remaining > 0 ? `${rock.weeks_remaining} weeks left` : 'Past due'}</span>
        <span>📝 {rock.checkin_count || 0} check-in{(rock.checkin_count || 0) !== 1 ? 's' : ''}</span>
      </div>

      {/* Action buttons */}
      {!isCompleted && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => onCheckin(rock)}
            disabled={!isCurrentQuarter}
            className={`py-1.5 px-4 text-white text-sm rounded-lg ${
              isCurrentQuarter
                ? 'bg-primary hover:bg-primary-dark'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid={`rock-checkin-btn-${rock.id}`}
          >
            Weekly Check-in
          </button>
          <button
            onClick={() => onComplete(rock)}
            disabled={!isCurrentQuarter}
            className={`py-1.5 px-4 text-white text-sm rounded-lg ${
              isCurrentQuarter
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid={`rock-complete-btn-${rock.id}`}
          >
            Mark Complete
          </button>
        </div>
      )}

      {/* Check-in timeline */}
      {checkins && checkins.length > 0 && (
        <div className="border-t pt-3 mt-2">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Check-in History</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {checkins.map((ci) => {
              const statusInfo = getStatusInfo(ci.status);
              return (
                <div key={ci.id} className="flex items-start gap-2 text-sm bg-gray-50 rounded-lg p-2" data-testid={`checkin-entry-${ci.id}`}>
                  <span className="mt-0.5">{statusInfo.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">{statusInfo.label}</span>
                      <span className="text-gray-400 text-xs">{ci.checkin_date}</span>
                    </div>
                    {ci.action_taken && (
                      <p className="text-gray-600 mt-0.5">{ci.action_taken}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RocksPage() {
  const { token } = useAuth();
  const [rocks, setRocks] = useState([]);
  const [checkinsByRock, setCheckinsByRock] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [checkinRock, setCheckinRock] = useState(null);

  // Quarter navigation state
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarterLabel());
  const currentQuarter = getCurrentQuarterLabel();
  const isCurrentQuarter = selectedQuarter === currentQuarter;

  // Helper: shift quarter by N quarters
  const shiftQuarter = (quarterStr, delta) => {
    const [year, q] = quarterStr.split('-Q').map(Number);
    const totalQuarters = year * 4 + q - 1;
    const newTotal = totalQuarters + delta;
    const newYear = Math.floor(newTotal / 4);
    const newQ = newTotal % 4 + 1;
    return `${newYear}-Q${newQ}`;
  };

  // Can only navigate to past quarters, not future
  const canGoForward = selectedQuarter < currentQuarter;

  const navigateQuarter = (direction) => {
    if (direction === 'prev') {
      setSelectedQuarter(prev => shiftQuarter(prev, -1));
    } else if (direction === 'next' && canGoForward) {
      setSelectedQuarter(prev => shiftQuarter(prev, 1));
    }
  };

  const goToCurrentQuarter = () => {
    setSelectedQuarter(currentQuarter);
  };

  const fetchRocks = useCallback(async () => {
    try {
      const res = await fetch(`/api/rocks?quarter=${selectedQuarter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch rocks');
      setRocks(data.rocks || []);

      // Fetch check-ins for each rock
      const checkinMap = {};
      for (const rock of (data.rocks || [])) {
        const cRes = await fetch(`/api/rocks/${rock.id}/checkins`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cRes.ok) {
          const cData = await cRes.json();
          checkinMap[rock.id] = cData.checkins || [];
        }
      }
      setCheckinsByRock(checkinMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, selectedQuarter]);

  useEffect(() => {
    fetchRocks();
  }, [fetchRocks]);

  const handleRockCreated = (newRock) => {
    setRocks(prev => [...prev, newRock]);
    setCheckinsByRock(prev => ({ ...prev, [newRock.id]: [] }));
  };

  const handleCheckinCreated = (rockId, checkin) => {
    setCheckinsByRock(prev => ({
      ...prev,
      [rockId]: [checkin, ...(prev[rockId] || [])],
    }));
    setRocks(prev => prev.map(r => r.id === rockId ? { ...r, checkin_count: (r.checkin_count || 0) + 1 } : r));
  };

  const handleComplete = async (rock) => {
    if (!window.confirm(`Mark "${rock.name}" as fully implemented?`)) return;
    try {
      const res = await fetch(`/api/rocks/${rock.id}/complete`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to complete rock');
      setRocks(prev => prev.map(r => r.id === rock.id ? data.rock : r));
    } catch (err) {
      setError(err.message);
    }
  };

  const existingCategories = rocks.map(r => r.category);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Quarter Navigation Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateQuarter('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300"
            data-testid="quarter-nav-prev"
            aria-label="Previous quarter"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Quarterly Rocks</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-testid="quarter-label">
              {isCurrentQuarter
                ? `${selectedQuarter} • ${rocks.length}/3 rocks set`
                : `${selectedQuarter} (Read-Only) • ${rocks.length}/3 rocks`
              }
            </p>
          </div>

          <button
            onClick={() => navigateQuarter('next')}
            disabled={!canGoForward}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="quarter-nav-next"
            aria-label="Next quarter"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Return to Current Quarter button */}
        {!isCurrentQuarter && (
          <div className="mt-3 text-center">
            <button
              onClick={goToCurrentQuarter}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors text-sm font-medium"
              data-testid="quarter-nav-current"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Back to Current Quarter
            </button>
          </div>
        )}
      </div>

      {/* Past Quarter Read-Only Banner */}
      {!isCurrentQuarter && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-center" data-testid="past-quarter-banner">
          <p className="text-sm text-amber-700 font-medium">
            {'🔒'} You are viewing a past quarter. Rocks are read-only.
          </p>
        </div>
      )}

      {/* Create Rock Button (only for current quarter) */}
      {isCurrentQuarter && rocks.length < 3 && (
        <div className="mb-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium flex items-center gap-2"
            data-testid="create-rock-btn"
          >
            <span className="text-lg">+</span> Add Rock
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {rocks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="text-5xl mb-4">🪨</div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Rocks Yet</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Quarterly rocks are big goals you commit to implementing over 90 days.
            Set one rock for each B4S category.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="py-2 px-6 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium"
            data-testid="create-rock-btn-empty"
          >
            Create Your First Rock
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rocks.map(rock => (
            <RockCard
              key={rock.id}
              rock={rock}
              checkins={checkinsByRock[rock.id] || []}
              onCheckin={setCheckinRock}
              onComplete={handleComplete}
              token={token}
              isCurrentQuarter={isCurrentQuarter}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateRockModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRockCreated}
          existingCategories={existingCategories}
          token={token}
        />
      )}

      {checkinRock && (
        <CheckinModal
          rock={checkinRock}
          onClose={() => setCheckinRock(null)}
          onCheckinCreated={handleCheckinCreated}
          token={token}
        />
      )}
    </div>
  );
}
